import { type FC, type MouseEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BottomEdgeHandle,
  LeftEdgeHandle,
  RightEdgeHandle,
  TopEdgeHandle,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'

import { EDGE_PILL_Z, createSyntheticMouseEvent } from './CommonControls'

type EdgeResizeControlsProps = {
  itemId: string
  itemsLocked: boolean
  zoom: number
}

type Pill = 'e' | 'w' | 'n' | 's'

/**
 * Edge resize handles ("pills") of an editor text box: the side pills
 * are a width resize, the top and bottom pills a height resize, and none
 * shows while the box is locked. A classic image's edge handles are
 * `classic/ClassicEdgeResizeControls.tsx`.
 *
 * Forwarding a pill's mousedown to the matching functional handle hands the
 * drag to react-resizable, and preventDefault on that mousedown keeps the
 * focus shift from blur-exiting an open edit session.
 */
export const EdgeResizeControls: FC<EdgeResizeControlsProps> = ({
  itemId,
  itemsLocked,
  zoom,
}) => {
  const { t } = useTranslation()

  const forwardToFunctionalHandle = useCallback(
    (placement: Pill) => (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const handle = document.querySelector(
        `[data-resize-handle="${itemId}"][data-resize-placement="${placement}"]`,
      )
      handle?.dispatchEvent(createSyntheticMouseEvent(e))
    },
    [itemId],
  )

  // Scale factor for zoom, clamped to prevent extremes
  const scale = Math.min(2, Math.max(0.5, 100 / zoom))

  if (itemsLocked) return null

  const widthLabel = t('itemControls.resizeWidth', 'Resize width')
  const heightLabel = t('itemControls.resizeHeight', 'Resize height')

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: `translateY(-50%) translateX(50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
          zIndex: EDGE_PILL_Z,
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip text={widthLabel} placement="right" offsetX={8}>
          <RightEdgeHandle
            onMouseDown={forwardToFunctionalHandle('e')}
            aria-label={widthLabel}
            data-testid="overlay-right-edge-handle"
          />
        </Tooltip>
      </div>

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: `translateY(-50%) translateX(-50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
          zIndex: EDGE_PILL_Z,
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip text={widthLabel} placement="left" offsetX={8}>
          <LeftEdgeHandle
            onMouseDown={forwardToFunctionalHandle('w')}
            aria-label={widthLabel}
            data-testid="overlay-left-edge-handle"
          />
        </Tooltip>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: `translateX(-50%) translateY(-50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
          zIndex: EDGE_PILL_Z,
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip text={heightLabel} placement="above" offsetY={8}>
          <TopEdgeHandle
            onMouseDown={forwardToFunctionalHandle('n')}
            aria-label={heightLabel}
            data-testid="overlay-top-edge-handle"
          />
        </Tooltip>
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: `translateX(-50%) translateY(50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
          zIndex: EDGE_PILL_Z,
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip text={heightLabel} placement="bottom" offsetY={8}>
          <BottomEdgeHandle
            onMouseDown={forwardToFunctionalHandle('s')}
            aria-label={heightLabel}
            data-testid="overlay-bottom-edge-handle"
          />
        </Tooltip>
      </div>
    </>
  )
}
